import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useMutation } from '@tanstack/react-query';

import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';
import { Text, TextType } from 'ui-components/components/text';

import { EventSourceStrings } from '../../../../../../tracking/tracking-constants';
import { useReturnsTabContext } from '../hooks/use-returns-tab-context';
import { getRetailerConfiguration, deactivateEnhancedReturnsForStore } from '../../../../../../store/returns/returns.api';
import useTrackReturnsSettings from '../hooks/use-track-returns-settings';
import { refreshStoreList } from '../../../../../../store/seller/seller.actions';
import { Segment } from '../../../../../../services/analytics-services/segment';
import { DEACTIVATE_ENHANCED_RETURNS_CLICK } from '../../../../../../constants/analytics-events';
import { SsrSignOnButton } from '../ssr-sign-on-button';
import { ReadOnlyPortalUrl } from '../read-only-portal-url';
import { ReturnsTabSection } from '../returns-tab-section';
import { ReturnsTabHeader } from '../returns-tab-header';
import { ReturnsTabLearnMoreLink } from '../returns-tab-learn-more-link';
import { ReturnsTabExpandedSection } from '../returns-tab-expanded-section';
import { ActionButton } from './action-button';

import messages from './messages';
import style from './style.module.less';

const RecommendedBadge = () => (
  <div className={style.recommendedBadge}>
    <Text textType={TextType.Caption} value={messages.recommendedBadge} />
  </div>
);

/**
 * Enhanced Returns Section component
 * Displays the Enhanced Returns section in the Returns tab
 * @returns {React.ReactElement|null} The Enhanced Returns section or null if it shouldn't be displayed
 */
export const EnhancedReturnsPanel = () => {
  const dispatch = useDispatch();
  const {
    isEnhancedReturnsAvailableOnAnyPlan,
    isEnhancedReturnsFeatureEnabled,
    isShipstationReturnsEnabled,
    isStoreSetupCompleteForEnhancedReturns,
    storeGuid,
    marketplaceName,
    supportsEnhancedReturns,
  } = useReturnsTabContext();

  const displayEnhancedReturnsSection =
    supportsEnhancedReturns && (isEnhancedReturnsAvailableOnAnyPlan || isEnhancedReturnsFeatureEnabled) && !isShipstationReturnsEnabled;
  // TODO: I'm not sure this is correct. This renders a purple badge which is a color typically associated with upsell
  const showRecommendedBadge = !isStoreSetupCompleteForEnhancedReturns;

  /**
   * Mutations
   */
  const { isPending, mutate: deactivateStore } = useMutation({
    mutationFn: (guid) => deactivateEnhancedReturnsForStore(guid),
  });

  /**
   * Component functions
   */
  const onClickDeactivate = useCallback(() => {
    const toastTitle = messages.deactivateReturnsToastTitle;
    Segment.trackEvent(DEACTIVATE_ENHANCED_RETURNS_CLICK, {
      type: DEACTIVATE_ENHANCED_RETURNS_CLICK,
      storeGuid,
    });

    deactivateStore(storeGuid, {
      onSuccess: () => {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          title: <FormattedMessage {...toastTitle} />,
          content: <FormattedMessage {...messages.deactivateStoreSuccess} />,
        });
        dispatch(refreshStoreList());
      },
      onError: () => {
        NotificationService.showNotification({
          title: <FormattedMessage {...toastTitle} />,
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: <FormattedMessage {...messages.deactivateStoreError} />,
        });
      },
    });
  }, [deactivateStore, dispatch, storeGuid]);

  /**
   * Don't display the Enhanced Returns Section if enhanced returns are not supported for this store or if the user is not on a plan
   * that supports enhanced returns.
   */
  if (!displayEnhancedReturnsSection) return null;

  return (
    <ReturnsTabSection>
      <ReturnsTabHeader>
        <div>
          <div className={style.returnsTabTitle}>
            <Text
              className={style.title}
              textType={TextType.H3}
              value={<FormattedMessage {...messages.advancedReturnsTitle} values={{ marketplaceName }} />}
            />
            {showRecommendedBadge && <RecommendedBadge />}
          </div>
          <Text className={style.returnsTabDescription} textType={TextType.Paragraph} value={messages.advancedReturnsDescription} />
          <ReturnsTabLearnMoreLink />
        </div>

        <ActionButton isLoading={isPending} onClick={onClickDeactivate} />
      </ReturnsTabHeader>

      {isStoreSetupCompleteForEnhancedReturns && <EnhancedReturnsPanelSettings />}
    </ReturnsTabSection>
  );
};

/**
 * Displays the Enhanced Returns panel settings.
 * @returns {React.ReactElement|null} The Enhanced Returns panel settings
 */
export const EnhancedReturnsPanelSettings = () => {
  const { storeGuid } = useReturnsTabContext();
  const [returnPortalUrl, setReturnPortalUrl] = useState('');
  const trackReturns = useTrackReturnsSettings(EventSourceStrings.STORE_SETTINGS_RETURNS_TAB);

  useEffect(() => {
    getRetailerConfiguration(storeGuid).then((response) => {
      const customerPortalDomains = response?.customer_portal_domains || [];
      setReturnPortalUrl(customerPortalDomains[0] ?? '');
    });
  }, [storeGuid]);

  return (
    <ReturnsTabExpandedSection className={style.row}>
      <ReadOnlyPortalUrl
        labelMessage={messages.advancedReturnsPortalUrlLabel}
        trackCopyUrl={trackReturns?.trackPortalUrlCopied}
        url={returnPortalUrl}
      />
      <SsrSignOnButton label={<FormattedMessage {...messages.configureReturnStore} />} storeGuid={storeGuid} />
    </ReturnsTabExpandedSection>
  );
};
